import Database from './database';
import { SyncOperation } from './database';
import TaskStorage from './TaskStorage';
import {StoreName} from "./StoreName";

class SyncService {
	private db: Database;
	private isSyncing = false;
	private retryDelay = 5000; // 5 секунд
	private maxRetryDelay = 60000; // 1 минута

	constructor() {
		this.db = Database.getInstance();
	}

	// Начать фоновую синхронизацию
	startBackgroundSync(): void {
	  return ;
		// Проверяем онлайн-статус
		if (!navigator.onLine) {
			console.log('Offline mode - delaying sync');
			setTimeout(() => this.startBackgroundSync(), this.retryDelay);
			return;
		}

		if (this.isSyncing) {
			return;
		}

		this.isSyncing = true;

		// Запускаем синхронизацию в фоне
		this.sync().finally(() => {
			this.isSyncing = false;

			// Повторяем через некоторое время
			setTimeout(() => this.startBackgroundSync(), 30000); // 30 секунд
		});
	}

	// Принудительная синхронизация
	async forceSync(): Promise<void> {
		if (this.isSyncing) {
			throw new Error('Sync already in progress');
		}

		this.isSyncing = true;

		try {
			await this.sync();
		} finally {
			this.isSyncing = false;
		}
	}

	// Основной метод синхронизации
	private async sync(): Promise<void> {
		const db = await this.db.init();

		try {
			// Получаем операции для синхронизации (максимум 10 за раз)
			const operations = await this.getSyncOperations(10);

			if (operations.length === 0) {
				console.log('No operations to sync');
				return;
			}

			console.log(`Syncing ${operations.length} operations...`);

			// Синхронизируем каждую операцию
			for (const operation of operations) {
				await this.syncOperation(operation);
			}

			// Обновляем метаданные о последней синхронизации
			await this.updateLastSyncTime();
		} catch (error) {
			console.error('Sync error:', error);
			throw error;
		}
	}

	// Получение операций для синхронизации
	private async getSyncOperations(limit: number): Promise<SyncOperation[]> {
		const db = await this.db.init();

		try {
			const index = db.transaction(StoreName.SyncQueue).store.index('by-priority');
			let cursor = await index.openCursor(null, 'next');

			const operations: SyncOperation[] = [];
			while (cursor && operations.length < limit) {
				// Пропускаем операции, которые уже были попробованы слишком много раз
				if (cursor.value.attempts < cursor.value.maxAttempts) {
					operations.push(cursor.value);
				}
				cursor = await cursor.continue();
			}

			return operations;
		} catch (error) {
			console.error('Error getting sync operations:', error);
			return [];
		}
	}

	// Синхронизация одной операции
	private async syncOperation(operation: SyncOperation): Promise<void> {
		try {
			// Здесь будет реальный вызов API
			// Для этапа 4 просто имитируем успешную синхронизацию
			await this.mockApiCall(operation);

			// Удаляем операцию из очереди после успешной синхронизации
			await this.markOperationAsSynced(operation.id);

			console.log(`Operation ${operation.id} synced successfully`);
		} catch (error) {
			console.error(`Failed to sync operation ${operation.id}:`, error);

			// Увеличиваем счетчик попыток
			await this.incrementOperationAttempts(operation.id, (error as any).message);

			// Если это последняя попытка, отмечаем операцию как неудачную
			if (operation.attempts + 1 >= operation.maxAttempts) {
				await this.markOperationAsFailed(operation.id, (error as any).message);
			}
		}
	}

	// Имитация вызова API
	private async mockApiCall(operation: SyncOperation): Promise<void> {
		// Имитируем задержку сети
		await new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 500));

		// Имитируем случайные ошибки (10% вероятность)
		if (Math.random() < 0.1) {
			throw new Error('Mock API error: Network timeout');
		}

		// Для задач, которые создаются, генерируем serverId
		if (operation.entityType === 'task' && operation.operation === 'create') {
			const task = operation.data;
			if (task) {
				task.serverId = Math.floor(Math.random() * 10000) + 1000;
			}
		}
	}

	// Пометить операцию как синхронизированную
	private async markOperationAsSynced(operationId: string): Promise<void> {
		const db = await this.db.init();

		try {
			await db.delete(StoreName.SyncQueue, operationId);

			// Если это была операция создания задачи, обновляем serverId
			const operation = await db.get(StoreName.SyncQueue, operationId);
			if (!operation) {
				return;
			}

			if (operation.entityType === 'task' && operation.operation === 'create') {
				const taskStorage = new TaskStorage();
				await taskStorage.markTaskAsSynced(operation.entityId, operation.data.serverId);
			}
		} catch (error) {
			console.error('Error marking operation as synced:', error);
			throw error;
		}
	}

	// Увеличить счетчик попыток
	private async incrementOperationAttempts(
		operationId: string,
		errorMessage?: string
	): Promise<void> {
		const db = await this.db.init();
		const transaction = db.transaction(StoreName.SyncQueue, 'readwrite');

		try {
			const operation = await transaction.store.get(operationId);
			if (!operation) {
				return;
			}

			const updatedOperation: SyncOperation = {
				...operation,
				attempts: operation.attempts + 1,
				error: errorMessage,
				updatedAt: new Date(),
			};

			await transaction.store.put(updatedOperation);
			await transaction.done;
		} catch (error) {
			console.error('Error incrementing operation attempts:', error);
			throw error;
		}
	}

	// Пометить операцию как неудачную
	private async markOperationAsFailed(operationId: string, errorMessage: string): Promise<void> {
		const db = await this.db.init();

		try {
			// Здесь можно добавить логику для обработки окончательно неудачных операций
			// Например, отправить уведомление пользователю
			console.warn(`Operation ${operationId} failed after maximum attempts: ${errorMessage}`);

			// Пока просто удаляем из очереди
			await db.delete(StoreName.SyncQueue, operationId);
		} catch (error) {
			console.error('Error marking operation as failed:', error);
			throw error;
		}
	}

	// Обновление времени последней синхронизации
	private async updateLastSyncTime(): Promise<void> {
		const db = await this.db.init();

		try {
			const metadata = {
				key: 'lastSyncTime',
				value: new Date().toISOString(),
				updatedAt: new Date(),
			};

			await db.put(StoreName.Metadata, metadata);
		} catch (error) {
			console.error('Error updating last sync time:', error);
			throw error;
		}
	}

	// Получение времени последней синхронизации
	async getLastSyncTime(): Promise<Date | null> {
		const db = await this.db.init();

		try {
			const metadata = await db.get(StoreName.Metadata, 'lastSyncTime');
			return metadata ? new Date(metadata.value) : null;
		} catch (error) {
			console.error('Error getting last sync time:', error);
			return null;
		}
	}

	// Получение статуса синхронизации
	async getSyncStatus(): Promise<{
    pending: number;
    failed: number;
    lastSync: Date | null;
    isOnline: boolean;
  }> {
		const db = await this.db.init();

		try {
			const operations = await db.getAll(StoreName.SyncQueue);
			const pending = operations.length;
			const failed = operations.filter(op => op.attempts >= op.maxAttempts).length;
			const lastSync = await this.getLastSyncTime();

			return {
				pending,
				failed,
				lastSync,
				isOnline: navigator.onLine,
			};
		} catch (error) {
			console.error('Error getting sync status:', error);
			return {
				pending: 0,
				failed: 0,
				lastSync: null,
				isOnline: navigator.onLine,
			};
		}
	}

	// Очистка очереди синхронизации
	async clearSyncQueue(): Promise<void> {
		const db = await this.db.init();

		try {
			const operations = await db.getAll(StoreName.SyncQueue);
			for (const operation of operations) {
				await db.delete(StoreName.SyncQueue, operation.id);
			}

			console.log('Sync queue cleared');
		} catch (error) {
			console.error('Error clearing sync queue:', error);
			throw error;
		}
	}
}

export default SyncService;
